import { Collapse, Form, Tooltip } from "antd";
import { useContext, useMemo } from "react";
import { sortLinkedItems } from "utils/utils";
import { Link, useParams } from "react-router-dom";
import { ErrorMessages } from "constants/index";
import { RiskLevelType } from "components/date-block/models";
import linkedCell from "components/links/linkedCell";
import LinkIcon from "components/svg-icons/link-icon";
import AsterixIcon from "components/svg-icons/asterix-icon";
import { ProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import { specSectionLabel } from "components/cell-renders";

// Helper component for the linking action button
function LinkingActionButton({
  projectId,
  featureInstanceData,
  linkText,
  testId,
  isMaterialsFeature
}: {
  projectId: string;
  featureInstanceData: any;
  linkText: string;
  testId: string;
  isMaterialsFeature: boolean;
}) {
  if (isMaterialsFeature) {
    return (
      <Link
        to={`/project/${projectId}/schedule/material-linking?&entityId=${featureInstanceData?.id}&source=material`}
        data-testid={testId}
      >
        {linkText}
      </Link>
    );
  }
  return (
    <Link
      to={`/project/${projectId}/schedule/submittal-linking?&SubmittalId=${featureInstanceData?.id}`}
      data-testid={testId}
    >
      {linkText}
    </Link>
  );
}

// Helper component for empty materials state
function EmptyMaterialsState({
  featureInstanceData,
  submittalInfo,
  projectId,
  canEditLinkingPage,
  isRevisionMode,
  isVoidSubmittal,
  isDeletedSubmittal,
  isTrackedSubmittal
}: {
  featureInstanceData: any;
  submittalInfo: any;
  projectId: string;
  canEditLinkingPage: boolean;
  isRevisionMode: boolean;
  isVoidSubmittal: boolean;
  isDeletedSubmittal: boolean;
  isTrackedSubmittal: boolean;
}) {
  const isMaterialsFeature =
    featureInstanceData?.feature_type_id === FeatureTypes.MATERIALS;
  const canShowLink = isMaterialsFeature
    ? canEditLinkingPage
    : canEditLinkingPage &&
      !isRevisionMode &&
      !isVoidSubmittal &&
      !isDeletedSubmittal &&
      isTrackedSubmittal;

  const getMessageAndLinkText = () => {
    if (isMaterialsFeature) {
      return {
        messageText: "No links added",
        linkText: "Click to add"
      };
    }

    return {
      messageText: submittalInfo?.material_tracking
        ? "No links added"
        : "Material tracking is disabled",
      linkText: submittalInfo?.material_tracking
        ? "Click to add"
        : "Click here to enable"
    };
  };

  const { messageText, linkText } = getMessageAndLinkText();

  return (
    <span className="text-xs text-neutral-400">
      {messageText}{" "}
      {canShowLink && (
        <LinkingActionButton
          projectId={projectId}
          featureInstanceData={featureInstanceData}
          linkText={linkText}
          testId="featurelinkedmaterials-link-enable-material-tracking"
          isMaterialsFeature={isMaterialsFeature}
        />
      )}
    </span>
  );
}

export default function FeatureMaterialsLinks(props: {
  featureInstanceData: any;
  canEditLinkingPage: boolean;
  isRevisionMode: boolean;
  isVoidSubmittal: boolean;
  isDeletedSubmittal: boolean;
  isTrackedSubmittal: boolean;
}) {
  const {
    featureInstanceData,
    canEditLinkingPage,
    isRevisionMode,
    isVoidSubmittal,
    isDeletedSubmittal,
    isTrackedSubmittal
  } = props;

  const { projectId } = useParams() as any;
  const cssForValue = "text-xs font-normal text-black text-opacity-80";
  const { Panel } = Collapse;
  const { projectDetails } = useContext(ProjectContext);

  const submittalInfo = useMemo(() => {
    if (!featureInstanceData?.feature_submittals?.length) return null;
    return featureInstanceData?.feature_submittals[0];
  }, [featureInstanceData]);

  const materialLinks = useMemo(() => {
    if (featureInstanceData?.feature_type_id === FeatureTypes.MATERIALS) {
      const {
        predecessor_material_links: predecessorMaterialLinks,
        successor_material_links: successorMaterialLinks
      } = featureInstanceData;

      // Early return if no links exist
      if (
        !predecessorMaterialLinks?.length &&
        !successorMaterialLinks?.length
      ) {
        return [];
      }

      // Process predecessor materials with optimized mapping
      const predecessorMaterials =
        predecessorMaterialLinks?.map((link: any) => ({
          ...link?.source_feature_instance,
          predecessor_material: true,
          governing_link: link?.governing_link
        })) || [];

      // Process successor materials with optimized mapping
      const successorMaterials =
        successorMaterialLinks?.map((link: any) => ({
          ...link?.target_feature_instance,
          predecessor_material: false,
          governing_link: link?.governing_link
        })) || [];

      // Combine arrays efficiently
      const allMaterials = predecessorMaterials.concat(successorMaterials);

      return sortLinkedItems(allMaterials, "auto_inc_value");
    }
    const linksAsSource = featureInstanceData?.links_as_source;
    if (!linksAsSource?.length) {
      return [];
    }

    const mappedItems = linksAsSource
      .filter(
        (link: any) =>
          link?.target_feature_instance?.feature_type_id ===
          FeatureTypes.MATERIALS
      )
      .map((link: any) => ({
        ...link?.target_feature_instance,
        governing_link: link?.governing_link
      }));

    if (!mappedItems.length) {
      return [];
    }

    if (mappedItems.length > 1) {
      const nonImplicitLinks = mappedItems.filter(
        (link: any) => !link?.implicit
      );
      return sortLinkedItems(nonImplicitLinks, "auto_inc_value");
    }

    return sortLinkedItems(mappedItems, "auto_inc_value");
  }, [featureInstanceData]);

  const materialLinkGroupTitle =
    featureInstanceData?.feature_type_id !== FeatureTypes.MATERIALS
      ? null
      : featureInstanceData?.predecessor_material_links?.length
        ? "Predecessors"
        : featureInstanceData?.successor_material_links?.length
          ? "Successors"
          : null;

  const getShadowMaterialName = (link: any) => {
    const showSpecNo = projectDetails?.spec_section;
    const specSectionObj = link?.feature_instance_spec_sections.length
      ? link?.feature_instance_spec_sections[0]?.spec_section
      : null;

    const submittalRefrenceId =
      showSpecNo && specSectionObj?.section_number
        ? `${specSectionObj?.section_number} - ${featureInstanceData?.auto_inc_value}`
        : featureInstanceData?.auto_inc_value;
    const materialName = `Material for Submittal ${submittalRefrenceId ?? ""}`;

    return (
      <div className="w-full material-name-input">
        <div className="flex w-full truncate px-1  items-center">
          <Tooltip title={materialName}>
            <span className="px-4 w-16 py-1 grow truncate">{materialName}</span>
          </Tooltip>
        </div>
      </div>
    );
  };

  const materialPanelHeader = (link: any) => {
    const matFullName = link.auto_inc_value
      ? `${link.auto_inc_value}-${link.title}`
      : link.title;
    const title: string = link.implicit ? "" : matFullName;
    return (
      <div className="flex w-full min-w-0 items-center" title={title}>
        <span className="flex shrink-0 items-center mr-2">
          <LinkIcon />
        </span>

        {link.implicit ? (
          getShadowMaterialName(link)
        ) : (
          <>
            {link?.auto_inc_value && (
              <i className="opacity-60">
                {link?.auto_inc_value}
                {!link.governing_link && <span className="mx-1">-</span>}
                {link.governing_link && link.predecessor_material && (
                  <span className="mx-1">-</span>
                )}
              </i>
            )}
            {link.governing_link && !link.predecessor_material && (
              <span className="flex items-center ml-2 mr-1 mb-1">
                <AsterixIcon />
              </span>
            )}
            <span className="min-w-0 flex-1 truncate">{link.title}</span>
          </>
        )}
      </div>
    );
  };

  return (
    <Form layout="vertical" data-testid="fml-form">
      <Form.Item
        label={
          <span>
            Materials
            {materialLinks?.length ? ` (${materialLinks?.length})` : ""}
          </span>
        }
      >
        {materialLinks?.length > 0 ? (
          <>
            {materialLinkGroupTitle && (
              <div
                data-testid="material-details-link-group-label"
                className="mb-2 mt-2 text-xs font-normal italic text-ciq-black-a88"
              >
                {materialLinkGroupTitle}
              </div>
            )}
            <Collapse
              accordion
              bordered={false}
              expandIconPlacement="end"
              className="custom-collapse"
              data-testid="fml-collapse"
            >
              {materialLinks?.map((link: any) => {
                // materialLinks is always built from spread objects ({...x}), so
                // every entry is a truthy object; this null-link guard never fires.
                /* v8 ignore next 19 */
                if (!link)
                  return (
                    <Panel
                      header={
                        <Tooltip
                          title={
                            !link.implicit
                              ? ErrorMessages.MaterialAccessDenied
                              : ""
                          }
                        >
                          {materialPanelHeader(link)}
                        </Tooltip>
                      }
                      key={link.id}
                      className="custom-panel"
                      collapsible="disabled"
                    />
                  );

                const materialReleaseStatus =
                  link?.feature_workflow_instance?.milestone_state || "";
                const materialRiskLevel =
                  link?.feature_workflow_instance?.risk_level || "";

                const specSectionObj = link?.feature_instance_spec_sections
                  .length
                  ? link?.feature_instance_spec_sections[0]?.spec_section
                  : null;

                const specSection = specSectionLabel({
                  specNumber: specSectionObj?.section_number,
                  specName:
                    specSectionObj?.section_name ?? specSectionObj?.title
                });

                const additionalPanelProps: any = {};
                if (link.implicit) {
                  additionalPanelProps.collapsible = "disabled";
                }
                return (
                  <Panel
                    header={materialPanelHeader(link)}
                    key={link.id}
                    className={
                      materialRiskLevel === RiskLevelType.High
                        ? "custom-panel risk-panel"
                        : "custom-panel"
                    }
                    {...additionalPanelProps}
                  >
                    <div
                      className="space-y-2.5"
                      data-testid="fml-panel-content"
                    >
                      <div className="grid grid-rows-3 -space-y-1">
                        <div className="flex w-full">
                          {linkedCell(
                            "Spec Section:",
                            `${specSection}`,
                            "",
                            `${cssForValue} max-w-[300px] truncate`
                          )}
                        </div>
                        <div className="flex w-full">
                          {linkedCell(
                            "Status:",
                            materialReleaseStatus,
                            "",
                            cssForValue
                          )}
                        </div>
                        <div className="flex w-full justify-between">
                          {linkedCell(
                            "Risk:",
                            materialRiskLevel,
                            "",
                            materialRiskLevel === RiskLevelType.High
                              ? "date-block-risk-high text-xs font-semibold"
                              : cssForValue
                          )}
                          {!isRevisionMode && !link?.implicit ? (
                            <Link
                              className="border-solid border-gray-300 hover:text-black hover:border-black border-[1px] rounded px-2 py-1 text-xs text-black"
                              to={{
                                pathname: `/project/${projectId}/materials/${link?.id}`
                              }}
                              data-testid="featurelinkedmaterials-link-view-material"
                            >
                              View Material
                            </Link>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </Panel>
                );
              })}
            </Collapse>
          </>
        ) : (
          <EmptyMaterialsState
            featureInstanceData={featureInstanceData}
            submittalInfo={submittalInfo}
            projectId={projectId}
            canEditLinkingPage={canEditLinkingPage}
            isRevisionMode={isRevisionMode}
            isVoidSubmittal={isVoidSubmittal}
            isDeletedSubmittal={isDeletedSubmittal}
            isTrackedSubmittal={isTrackedSubmittal}
          />
        )}
      </Form.Item>
    </Form>
  );
}
